import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Divider,
  Button,
  IconButton,
  Dialog,
  DialogContent,
  DialogTitle,
  TextField,
  useTheme,
  useMediaQuery,
  Stack,
} from '@mui/material';
import {
  Search as SearchIcon,
  Close as CloseIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
} from '@mui/icons-material';

interface SearchParams {
  location?: string;
  checkIn?: string;
  checkOut?: string;
  guests?: number;
}

interface SegmentedSearchBarProps {
  onSearch?: (params: SearchParams) => void;
}

export const SegmentedSearchBar: React.FC<SegmentedSearchBarProps> = ({ onSearch }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [location, setLocation] = useState('');
  const [checkIn, setCheckIn] = useState('');
  const [checkOut, setCheckOut] = useState('');
  const [guests, setGuests] = useState(1);
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleSearch = (): void => {
    onSearch?.({ location, checkIn, checkOut, guests });
    setMobileOpen(false);
  };

  const summaryText = [
    location || 'Anywhere',
    checkIn ? new Date(checkIn).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : 'Any week',
    `${guests} guest${guests !== 1 ? 's' : ''}`,
  ].join(' · ');

  if (isMobile) {
    return (
      <>
        {/* Compact mobile trigger */}
        <Paper
          onClick={() => setMobileOpen(true)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            px: 2.5,
            py: 1.5,
            borderRadius: 50,
            cursor: 'pointer',
            bgcolor: 'rgba(255,255,255,0.95)',
            backdropFilter: 'blur(8px)',
            transition: 'box-shadow 200ms ease',
            '&:hover': { boxShadow: '0 4px 16px rgba(0,0,0,0.12)' },
          }}
        >
          <SearchIcon sx={{ color: 'text.secondary' }} />
          <Typography variant="body2" color="text.primary" sx={{ fontWeight: 500 }}>
            {summaryText}
          </Typography>
        </Paper>

        {/* Full-screen mobile search sheet */}
        <Dialog
          fullScreen
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          PaperProps={{ sx: { bgcolor: '#fafafa' } }}
        >
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6" fontWeight={600}>Search</Typography>
            <IconButton onClick={() => setMobileOpen(false)} aria-label="close search">
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <Stack spacing={3} sx={{ mt: 1 }}>
              <TextField
                label="Where"
                placeholder="Search destinations"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                fullWidth
                sx={{ '& .MuiOutlinedInput-root': { minHeight: 48 } }}
              />
              <TextField
                label="Check in"
                type="date"
                value={checkIn}
                onChange={(e) => setCheckIn(e.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
                sx={{ '& .MuiOutlinedInput-root': { minHeight: 48 } }}
              />
              <TextField
                label="Check out"
                type="date"
                value={checkOut}
                onChange={(e) => setCheckOut(e.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
                sx={{ '& .MuiOutlinedInput-root': { minHeight: 48 } }}
              />
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Guests
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <IconButton
                    size="small"
                    onClick={() => setGuests(Math.max(1, guests - 1))}
                    disabled={guests <= 1}
                    aria-label="decrease guests"
                  >
                    <RemoveIcon />
                  </IconButton>
                  <Typography variant="h6" sx={{ minWidth: 32, textAlign: 'center' }}>
                    {guests}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() => setGuests(guests + 1)}
                    aria-label="increase guests"
                  >
                    <AddIcon />
                  </IconButton>
                </Box>
              </Box>
              <Button
                variant="contained"
                size="large"
                fullWidth
                startIcon={<SearchIcon />}
                onClick={handleSearch}
                sx={{ mt: 2, py: 1.5 }}
              >
                Search
              </Button>
            </Stack>
          </DialogContent>
        </Dialog>
      </>
    );
  }

  // Desktop: pill-shaped segmented bar
  return (
    <Paper
      elevation={0}
      sx={{
        display: 'flex',
        alignItems: 'center',
        borderRadius: 50,
        bgcolor: 'rgba(255,255,255,0.95)',
        backdropFilter: 'blur(8px)',
        overflow: 'hidden',
        border: '1px solid rgba(0,0,0,0.08)',
        transition: 'box-shadow 200ms ease',
        '&:hover': { boxShadow: '0 4px 16px rgba(0,0,0,0.12)' },
      }}
    >
      {/* Where */}
      <Box sx={{ flex: 1, px: 3, py: 1.5 }}>
        <Typography variant="caption" fontWeight={600} color="text.primary">
          Where
        </Typography>
        <TextField
          variant="standard"
          placeholder="Search destinations"
          value={location}
          onChange={(e) => setLocation(e.target.value)}
          fullWidth
          InputProps={{ disableUnderline: true, sx: { fontSize: '0.875rem' } }}
        />
      </Box>

      <Divider orientation="vertical" flexItem sx={{ my: 1.5 }} />

      {/* Check in */}
      <Box sx={{ px: 3, py: 1.5, minWidth: 140 }}>
        <Typography variant="caption" fontWeight={600} color="text.primary">
          Check in
        </Typography>
        <TextField
          variant="standard"
          type="date"
          value={checkIn}
          onChange={(e) => setCheckIn(e.target.value)}
          fullWidth
          InputProps={{ disableUnderline: true, sx: { fontSize: '0.875rem' } }}
        />
      </Box>

      <Divider orientation="vertical" flexItem sx={{ my: 1.5 }} />

      {/* Check out */}
      <Box sx={{ px: 3, py: 1.5, minWidth: 140 }}>
        <Typography variant="caption" fontWeight={600} color="text.primary">
          Check out
        </Typography>
        <TextField
          variant="standard"
          type="date"
          value={checkOut}
          onChange={(e) => setCheckOut(e.target.value)}
          fullWidth
          InputProps={{ disableUnderline: true, sx: { fontSize: '0.875rem' } }}
        />
      </Box>

      <Divider orientation="vertical" flexItem sx={{ my: 1.5 }} />

      {/* Guests */}
      <Box sx={{ px: 3, py: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box>
          <Typography variant="caption" fontWeight={600} color="text.primary">
            Guests
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <IconButton size="small" onClick={() => setGuests(Math.max(1, guests - 1))} disabled={guests <= 1} aria-label="decrease guests">
              <RemoveIcon fontSize="small" />
            </IconButton>
            <Typography variant="body2" sx={{ minWidth: 20, textAlign: 'center' }}>
              {guests}
            </Typography>
            <IconButton size="small" onClick={() => setGuests(guests + 1)} aria-label="increase guests">
              <AddIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>
      </Box>

      {/* Search button */}
      <Button
        variant="contained"
        onClick={handleSearch}
        aria-label="search"
        sx={{
          borderRadius: 50,
          minWidth: 48,
          minHeight: 48,
          mr: 1,
          px: 3,
        }}
      >
        <SearchIcon />
      </Button>
    </Paper>
  );
};
